import React, { useEffect, useId, useRef, useSyncExternalStore } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';
import { apiErrors } from '../services/apiErrors';
import './ErrorPage.css';

interface Props { status?: number; onClose?: () => void; mutation?: boolean }

export function ErrorPage({ status = 500, onClose, mutation = false }: Props) {
    const missing = status === 404;
    const titleId = useId();
    const title = missing ? 'Túto stránku sme nenašli' : 'Niečo sa nepodarilo';
    useEffect(() => {
        const previous = document.title;
        document.title = `${status} · ${title} | Flowy`;
        return () => { document.title = previous; };
    }, [status, title]);
    return (
        <section className="flowy-error-page" aria-labelledby={titleId}>
            <div className="flowy-error-card">
                <div className="flowy-error-brand">flowy<span>.</span></div>
                <div className="flowy-error-code" aria-hidden="true">{status}</div>
                <p className="flowy-error-label">{missing ? 'Stránka alebo záznam neexistuje' : 'Služba je dočasne nedostupná'}</p>
                <h1 id={titleId}>{title}</h1>
                <p className="flowy-error-description">{missing
                    ? 'Odkaz už nemusí byť platný alebo bol záznam odstránený.'
                    : 'Skúste to o chvíľu znova. Ak problém pretrváva, obráťte sa na správcu.'}</p>
                {mutation && <p className="flowy-error-description">Pred opakovaním akcie overte, či sa už vykonala.</p>}
                <div className="flowy-error-actions">
                    {onClose ? <>
                        {/* Zrušiť only closes the notice: the page and any unsaved form stay as they are */}
                        <button onClick={onClose} autoFocus>Zrušiť</button>
                        <a className="flowy-error-primary" href="/">Späť do aplikácie</a>
                    </> : <a className="flowy-error-primary" href="/">Prejsť na úvod</a>}
                    {!missing && !mutation && <button onClick={() => window.location.reload()}>Obnoviť stránku</button>}
                </div>
                <p className="flowy-error-footnote">{onClose ? 'Po zrušení zostane vaša otvorená stránka zachovaná.' : 'Flowy · Všetko na jednom mieste'}</p>
            </div>
        </section>
    );
}

export function RouteErrorPage() {
    const error = useRouteError();
    return <ErrorPage status={isRouteErrorResponse(error) ? error.status : 500} />;
}

export class AppErrorBoundary extends React.Component<{ children: React.ReactNode }, { failed: boolean }> {
    state = { failed: false };
    static getDerivedStateFromError() { return { failed: true }; }
    render() { return this.state.failed ? <ErrorPage /> : this.props.children; }
}

export function ApiErrorNotice() {
    const failure = useSyncExternalStore(apiErrors.subscribe, apiErrors.getSnapshot);
    const dialog = useRef<HTMLDialogElement>(null);
    useEffect(() => {
        if (failure && dialog.current && !dialog.current.open) dialog.current.showModal();
    }, [failure]);
    if (!failure) return null;
    // Keep the router/form mounted: a failed save must not discard the user's inputs.
    return <dialog ref={dialog} className="flowy-error-dialog" aria-label={`Chyba ${failure.status}`} onCancel={apiErrors.dismiss} onClose={apiErrors.dismiss}>
        <ErrorPage status={failure.status} mutation={failure.mutation} onClose={apiErrors.dismiss} />
    </dialog>;
}
